import type { Json, ServiceRegistry } from "../contracts";

// A button-dispatch engine. The dispatcher (`RUN_BUTTON` handler) probes the
// engine matching `payload.commandType` via `canHandle` and falls back to the
// `"default"` engine if the named engine cannot handle the dispatch. The
// default engine MUST always return true from `canHandle` so the chain has a
// guaranteed floor.
//
// Engines stay framework-agnostic (no React, no DOM) — they receive a service
// registry + light environment and return the raw server response. Navigation
// hint interpretation is a separate step (interpretServerAction) so a single
// engine does not have to know the whole response protocol.

// Payload shape that engines and the registry both speak. Mirrors
// `PlatformCommandPayloads.RUN_BUTTON` (kept in `contracts/command.ts`) but
// duplicated here so the engine module stays free of platform-command imports
// and can be tested in isolation.
export interface RunButtonPayload {
    actionSlug: string;
    recordUuid: string;
    commandName: string;
    commandType?: string | null;
    // Human label — used by the success-toast path for workflow transitions.
    // Engines themselves ignore it. Mirrors PlatformCommandPayloads.RUN_BUTTON.
    label?: string | null;
    context?: Json;
}

// Light env passed to engines — enough for them to talk to the right tenant /
// principal without depending on the React adapter. Extend cautiously: every
// field added here is a contract every engine sees.
export interface CommandEngineContext {
    services: ServiceRegistry;
    orgCode: string;
    tenantId?: string;
    principalId?: string;
}

export interface CommandEngine {
    key: string;
    // Cheap, sync, side-effect-free predicate. Default engine always returns
    // true — it is the floor of the resolution chain. Specialty engines say
    // `false` when they don't recognise the command, and the dispatcher then
    // falls through to default.
    canHandle(payload: RunButtonPayload, ctx: CommandEngineContext): boolean;
    dispatch(payload: RunButtonPayload, ctx: CommandEngineContext): Promise<unknown>;
}

export interface CommandEngineResolution {
    engine: CommandEngine;
    // True when the named (`payload.commandType`) engine couldn't handle the
    // dispatch and we degraded to default. Useful for telemetry — a mis-typed
    // `command-type` in DSL still works, but should be logged.
    fellBackToDefault: boolean;
}

// Thrown only when there is genuinely no engine to run the dispatch — i.e. no
// `default` engine was ever registered. In normal operation this is impossible
// because boot wires default first.
export class NoDefaultEngineError extends Error {
    constructor() {
        super("CommandEngineRegistry: no \"default\" engine registered — boot did not wire core engines.");
        this.name = "NoDefaultEngineError";
    }
}

export class CommandEngineRegistry {
    private readonly engines = new Map<string, CommandEngine>();

    register(engine: CommandEngine): () => void {
        this.engines.set(engine.key, engine);
        return () => {
            if (this.engines.get(engine.key) === engine) this.engines.delete(engine.key);
        };
    }

    has(key: string): boolean {
        return this.engines.has(key);
    }

    // Resolve the engine to run a given dispatch. Two-step chain:
    //   1. If payload.commandType is set AND the registered engine for that
    //      key reports canHandle === true → return it.
    //   2. Otherwise, return the "default" engine. (Default always canHandle.)
    // Throws NoDefaultEngineError ONLY if "default" was never registered.
    resolve(payload: RunButtonPayload, ctx: CommandEngineContext): CommandEngineResolution {
        const named = payload.commandType ? this.engines.get(payload.commandType) : undefined;
        if (named && named.canHandle(payload, ctx)) {
            return { engine: named, fellBackToDefault: false };
        }
        const fallback = this.engines.get("default");
        if (!fallback) throw new NoDefaultEngineError();
        return { engine: fallback, fellBackToDefault: Boolean(named) };
    }
}
